/** Auto-collapse editor chrome after entry; expand again when the bar is tapped. */

export const EDITOR_BANNER_COLLAPSE_MS = 4000;

export interface CollapseBannerHandle {
  collapse(): void;
  expand(): void;
  destroy(): void;
  isCollapsed(): boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target instanceof HTMLElement ? target : null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

/**
 * Full banner on entry, then a slim bar after {@link EDITOR_BANNER_COLLAPSE_MS} or the first
 * interaction on any of `interactionRoots` (pointer, wheel, or non-typing keyboard).
 */
export function mountCollapseBanner(
  banner: HTMLElement,
  interactionRoots: readonly (HTMLElement | Document | Window)[],
  opts: { delayMs?: number; initiallyCollapsed?: boolean } = {},
): CollapseBannerHandle {
  const delayMs = opts.delayMs ?? EDITOR_BANNER_COLLAPSE_MS;
  let collapsed = opts.initiallyCollapsed === true;
  let destroyed = false;
  let timer = 0;

  const apply = () => {
    banner.classList.toggle("is-collapsed", collapsed);
    banner.setAttribute("aria-expanded", collapsed ? "false" : "true");
  };

  const collapse = () => {
    if (destroyed || collapsed) return;
    collapsed = true;
    window.clearTimeout(timer);
    apply();
  };

  const expand = () => {
    if (destroyed || !collapsed) return;
    collapsed = false;
    apply();
    window.clearTimeout(timer);
    timer = window.setTimeout(collapse, delayMs);
  };

  const onInteract = (e: Event) => {
    if (e.type === "keydown" && isTypingTarget(e.target)) return;
    collapse();
  };

  const listeners: Array<{
    root: EventTarget;
    type: string;
    fn: (e: Event) => void;
    opts?: boolean | AddEventListenerOptions;
  }> = [];
  const add = (root: EventTarget, type: string, fn: (e: Event) => void, opts?: boolean | AddEventListenerOptions) => {
    root.addEventListener(type, fn, opts);
    listeners.push({ root, type, fn, opts });
  };

  for (const root of interactionRoots) {
    add(root, "pointerdown", onInteract, { capture: true });
    add(root, "wheel", onInteract, { passive: true, capture: true });
    add(root, "keydown", onInteract, { capture: true });
  }

  const collapsedHit = banner.querySelector<HTMLElement>(".editor-banner-collapsed");
  if (collapsedHit) {
    const open = (e: Event) => {
      if ((e.target as HTMLElement).closest("button, a")) return;
      e.preventDefault();
      expand();
    };
    add(collapsedHit, "click", open);
    add(collapsedHit, "keydown", (e) => {
      const key = (e as KeyboardEvent).key;
      if (key !== "Enter" && key !== " ") return;
      open(e);
    });
  }

  apply();
  if (!collapsed) timer = window.setTimeout(collapse, delayMs);

  return {
    collapse,
    expand,
    isCollapsed: () => collapsed,
    destroy: () => {
      destroyed = true;
      window.clearTimeout(timer);
      for (const { root, type, fn, opts } of listeners) root.removeEventListener(type, fn, opts);
      listeners.length = 0;
    },
  };
}

let adminPublishedSaveConfirmed = false;

/** One confirm per tab session before the first save on a published quest (admin live edit). */
export function confirmAdminPublishedSave(message: string): boolean {
  if (adminPublishedSaveConfirmed) return true;
  if (!window.confirm(message)) return false;
  adminPublishedSaveConfirmed = true;
  return true;
}

/** Test hook: reset the once-per-session admin save ack. */
export function resetAdminPublishedSaveConfirm(): void {
  adminPublishedSaveConfirmed = false;
}
